import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react";

import { applyGroupFilter } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceQuery } from "@formant/core/model/IDeviceQuery";
import { IGroup } from "@formant/core/model/IGroup";
import { Uuid } from "@formant/core/model/Uuid";

import { useRouter } from "../../context/RouterContext";
import { useStores } from "../../context/StoreContext";
import { Header } from "../common/Header";
import { Events, ICustomHeaderProps } from "../Events";
import { NotFound } from "../NotFound";
import { ViewMultiDeviceHeader } from "../ViewSmartFleet/ViewMultiDeviceHeader";

function isNotFound<T>(it: T | false): it is false {
    return it === false;
}

function isLoading<T>(it: T | undefined): it is undefined {
    return it === undefined;
}

interface IViewSmartFleetEventsProps {
    id: Uuid;
    className?: string;
}

export const ViewSmartFleetEvents = observer(function ViewSmartFleetEvents(
    props: IViewSmartFleetEventsProps,
) {
    const { id } = props;
    const { deviceStore, groupStore } = useStores();
    const { navigate, routeTo } = useRouter();
    const [group, setGroup] = useState<IGroup | undefined | false>(undefined);
    const [devices, setDevices] = useState<IDevice[] | undefined>(undefined);
    const [filter, setFilter] = useState<IDeviceQuery | undefined>(undefined);
    const onChangeViewId = useCallback(
        (newViewId: Uuid) => {
            fork(
                navigate(routeTo.smartFleetWithView({ id, viewId: newViewId })),
            );
        },
        [navigate, routeTo, id],
    );

    useEffect(() => {
        const fetchGroup = async () => {
            const data = await groupStore.fetchGet(id);
            setGroup(data);
            if (data) {
                const deviceQuery: IDeviceQuery = {
                    enabled: true,
                    type: "default",
                };

                const f = applyGroupFilter(data, deviceQuery);
                setFilter(f);
                const d = await deviceStore.fetchList(f);
                setDevices(d);
            }
        };
        void fetchGroup();
    }, [id]);

    const CustomHeader = useCallback(
        (innerProps: ICustomHeaderProps) => {
            const { title, className, children } = innerProps;
            if (!filter || !devices) {
                return null;
            }
            const deviceIds = devices.map(_ => defined(_.id)) || [];
            return (
                <>
                    <ViewMultiDeviceHeader
                        deviceIds={deviceIds}
                        groupId={id}
                        filter={filter}
                        context={filter}
                        onChangeViewId={onChangeViewId}
                        onChangeFilter={() => {}}
                    />
                    <Header
                        title={title}
                        className={className}
                        hideMenu={true}
                        hideTitle={true}
                    >
                        {children}
                    </Header>
                </>
            );
        },
        [devices, filter],
    );

    if (isLoading(group)) {
        return null; /* TODO: Spinner, its loading */
    }

    if (isNotFound(group)) {
        return <NotFound />;
    }

    if (isLoading(devices)) {
        return null;
    }

    const deviceIds = devices.map(_ => defined(_.id)) || [];

    return <Events deviceIds={deviceIds} CustomHeader={CustomHeader} />;
});
